import React from "react";
import MainContext from "../../Context/MainContext/MainContext";
import Dialog from "../Dialog";
import MUIAutocomplete from "@mui/material/Autocomplete";
import MUITextField from "@mui/material/TextField";
import MUIButton from "@mui/material/Button";
import MUIDialogActions from "@mui/material/DialogActions";
import { DateTimePicker as MUIDateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { TimePicker as MUITimePicker } from "@mui/x-date-pickers/TimePicker";

import User from "../../Utility/Crud/User";
import moment from "moment";

export default class DialogDateTimePickerAndAtp extends Dialog {
  static contextType = MainContext; // For legacy class components

  componentDidMount() {
    this.setState({
      atpId: this.getInitialAtpId(),
      timestamp: this.getInitialTimestamp(),
    });
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps.row !== this.props.row ||
      prevProps.atpId !== this.props.atpId ||
      prevProps.timestamp !== this.props.timestamp ||
      prevProps.salesLocationId !== this.props.salesLocationId
    ) {
      this.setState({
        atpId: this.getInitialAtpId(),
        timestamp: this.getInitialTimestamp(),
      });
    }
  }

  /**
   * Resolves the initial ATP identifier. When an explicit atpId prop is
   * provided (chart usage) it takes precedence; otherwise the value is read
   * from the queue row data structure.
   * @returns {string|null} The initial ATP identifier.
   */
  getInitialAtpId() {
    if (this.props.atpId !== undefined) {
      return this.props.atpId || null;
    }
    return this.props.row?.["order_activity.atp_id"] || null;
  }

  /**
   * Resolves the initial ATP evaluation timestamp. When an explicit timestamp
   * prop is provided (chart usage) it takes precedence; otherwise the value is
   * read from the queue row data structure.
   * @returns {string|null} The initial ATP evaluation timestamp.
   */
  getInitialTimestamp() {
    if (this.props.timestamp !== undefined) {
      return this.props.timestamp || null;
    }
    return this.props.row?.["order_activity.atp_evaluation_date"] || null;
  }

  /**
   * Resolves the sales location identifier used to filter the available ATP
   * options. When an explicit salesLocationId prop is provided (chart usage)
   * it takes precedence; otherwise the value is read from the queue row data
   * structure.
   * @returns {string|null} The sales location identifier.
   */
  getSalesLocationId() {
    if (this.props.salesLocationId !== undefined) {
      return this.props.salesLocationId || null;
    }
    return this.props.row?.["order_activity.sales_location_id"] || null;
  }

  /**
   * Generates options for ATP selection dropdown.
   * Ensures that the currently selected ATP is included in the options
   * even if they are not active.
   * @param {string} locationIds - The location IDs to filter ATP options.
   * @returns {Array} Array of ATP options with label and value.
   */
  getATPOptions(locationIds) {
    let users = User.getATPs(
      this.context.usersIndexed,
      this.context.userprofileLocationsIndexed,
      [locationIds],
    );
    const selectedUser = this.context.usersIndexed[this.state.atpId];
    if (!users.includes(selectedUser) && selectedUser) {
      users.push({ ...selectedUser });
    }

    return users;
  }

  /**
   * Handle date change, adjusting time as needed
   * @param {string|null} newTimestamp - The new timestamp selected.
   * @param {moment.Moment|null} correctedTimeValue - The corrected time value.
   * We determine whether or not a user selected a time by checking if there is a time value at second and millisecond.
   * We do this because users can never select seconds or milliseconds in the time picker,
   * so if they are set to anything other than 0, we know the user selected a time.
   */
  handleDateChange(newDate, correctedTimeValue) {
    const newTimestamp = this.toDataBase(newDate, correctedTimeValue);
    this.setState({ timestamp: newTimestamp });
  }

  /**
   * handles time value change.
   * @param {*} timeValue
   */
  handleTimeValueChange(timeValue) {
    const date = this.state.timestamp
      ? this.fromDataBase(this.state.timestamp)
      : moment().startOf("day");
    const newTimestamp = this.toDataBase(date, timeValue);
    this.setState({ timestamp: newTimestamp });
  }

  fromDataBase(timestamp) {
    return timestamp ? moment.utc(timestamp).local() : null;
  }

  toDataBase(date, time) {
    if (!date) return null;
    let newDate = moment(date).clone();
    if (!time || (time.millisecond() === 1 && time.second() === 1)) {
      // Cleared time: set to 00:00:01.001 as your special marker
      newDate.hour(0).minute(0).second(1).millisecond(1);
    } else {
      newDate.hour(time.hour()).minute(time.minute()).second(0).millisecond(0);
    }
    return newDate.utc().toISOString();
  }

  /**
   * Render main content text of dialog
   */
  renderContent() {
    const atpOptions = this.getATPOptions(this.getSalesLocationId());
    const atpValue =
      atpOptions.find((option) => option.id === this.state.atpId) || null;
    const timeValue = this.fromDataBase(this.state.timestamp);
    const correctedTimeValue = timeValue
      ? timeValue.second() === 1 && timeValue.millisecond() === 1
        ? null
        : timeValue
      : null;

    return (
      <>
        <MUIAutocomplete
          options={atpOptions}
          value={atpValue}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          getOptionLabel={(option) => User.getDisplayName(option) || ""}
          onChange={(e, newValue) => {
            this.setState({ atpId: newValue?.id || null });
            if (newValue === null) {
              this.setState({ timestamp: null });
            }
          }}
          required={true}
          sx={{ mt: 2 }}
          renderInput={(params) => (
            <MUITextField
              {...params}
              size="small"
              variant="standard"
              label="ATP"
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          )}
        />
        <MUIDateTimePicker
          onChange={(value) => {
            // value is local, convert to UTC string for state
            this.handleDateChange(value, correctedTimeValue);
          }}
          views={["year", "month", "day"]}
          sx={{ marginTop: 3 }}
          value={timeValue}
          disabled={!atpValue}
          label="Date"
          slotProps={{
            field: {
              clearable: true,
            },
            textField: {
              fullWidth: true,
              size: "small",
              variant: "standard",
              InputLabelProps: {
                shrink: true,
              },
            },
          }}
        />
        <MUITimePicker
          onChange={(timeValue) => {
            this.handleTimeValueChange(timeValue);
          }}
          views={["hours", "minutes"]}
          label="Time"
          disabled={!atpValue}
          sx={{ marginTop: 3 }}
          value={correctedTimeValue}
          slotProps={{
            field: {
              clearable: true,
            },
            textField: {
              fullWidth: true,
              size: "small",
              variant: "standard",
              InputLabelProps: {
                shrink: true,
              },
            },
          }}
        />
      </>
    );
  }

  /**
   * Render cancel and confirm button actions of dialog.
   */
  renderActions() {
    return (
      <MUIDialogActions spacing={2}>
        <MUIButton variant="text" onClick={this.props.onClose}>
          Cancel
        </MUIButton>
        <MUIButton
          variant="contained"
          loading={this.props.loading}
          onClick={() => {
            this.props.onConfirm({
              atpId: this.state.atpId,
              timestamp: this.state.timestamp,
            });
          }}
        >
          Confirm
        </MUIButton>
      </MUIDialogActions>
    );
  }

  /**
   * @returns {string} The max width of the dialog.
   */
  getMaxWidth() {
    return "xs";
  }

  /**
   * @returns {boolean} Whether or not to make dialog full width.
   */
  getFullWidth() {
    return false;
  }
}
